/**
 * The working indicator (DESIGN UD-11, UX-SPEC §15a): a spinner, what is running, and a rounded time estimate when one can
 * be made. It appears after 400 ms so quick operations do not flash it (at once for work that blocks the main thread). Accessible: the label is an `output` (a polite status) that is
 * announced once (the estimate and elapsed time are not live regions, so a screen reader is not read a new number every
 * second); the container is `aria-busy`. With reduced motion the spinner stands still and the text carries the update.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { type Estimate, createEstimator, indicatorVisible, overranPrior } from '../estimate.ts';
import { fmtElapsed } from '../format.ts';
import { WORKING } from '../strings.ts';
import { type WorkItem, work } from '../work.ts';

export function Spinner() {
	return <span class="spinner" aria-hidden="true" />;
}

/** The estimate of one running operation, refreshed every second and whenever its fraction changes. */
export function useEstimate(w: {
	startedAt: number;
	fraction: number | null;
	startFraction?: number;
	priorMs: number | null;
}): { now: number; estimate: Estimate | null } {
	const [now, setNow] = useState(performance.now());
	useEffect(() => {
		const t = window.setInterval(() => setNow(performance.now()), 1000);
		return () => window.clearInterval(t);
	}, []);
	const est = useRef<{ key: string; e: ReturnType<typeof createEstimator> } | null>(null);
	const key = `${w.startedAt}|${w.startFraction ?? 0}|${w.priorMs ?? ''}`;
	if (!est.current || est.current.key !== key)
		est.current = {
			key,
			e: createEstimator({
				startedAt: w.startedAt,
				startFraction: w.startFraction ?? 0,
				priorMs: w.priorMs,
			}),
		};
	const at = performance.now();
	return { now, estimate: est.current.e.update(Math.max(at, now), w.fraction) };
}

/** Re-renders on a short tick until the indicator is due, then once a second. */
function useDelayed(startedAt: number, delayMs: number): boolean {
	const [, tick] = useState(0);
	const due = indicatorVisible(performance.now() - startedAt, delayMs);
	useEffect(() => {
		if (due) return;
		const t = window.setTimeout(
			() => tick((n) => n + 1),
			Math.max(0, delayMs - (performance.now() - startedAt)) + 5
		);
		return () => window.clearTimeout(t);
	}, [due, startedAt, delayMs]);
	return due;
}

export function Working({ item }: { item: WorkItem }) {
	const due = useDelayed(item.startedAt, item.delayMs);
	const { now, estimate } = useEstimate(item);
	if (!due) return null;
	const body = (
		<>
			<Spinner />
			<output class="working-label">{item.label}…</output>
			{item.detail ? (
				<span class="working-detail" data-testid="working-detail">
					{item.detail}
				</span>
			) : null}
			{estimate ? (
				<span class="working-est" data-testid="working-estimate">
					{estimate.text}
				</span>
			) : item.hint ? (
				<span class="working-est" data-testid="working-hint">
					{item.hint}
				</span>
			) : overranPrior(now - item.startedAt, item.priorMs) ? (
				<span class="working-est" data-testid="working-longer">
					{WORKING.longerThanExpected}
				</span>
			) : null}
			<span class="working-elapsed dim">{WORKING.elapsed(fmtElapsed(now - item.startedAt))}</span>
			{item.onOpen ? (
				<span class="working-open" data-testid="working-open">
					{WORKING.details}
				</span>
			) : null}
		</>
	);
	// With a fuller status view (UD-20) the whole line is the control: one real button, so a click anywhere on it, or Enter or
	// Space on the focused line, opens the dialog. "Details" stays on it as the hint of what a click does.
	return item.onOpen ? (
		<button
			type="button"
			class="working working-button"
			data-testid="working"
			data-op={item.id}
			aria-busy="true"
			aria-haspopup="dialog"
			title={WORKING.openHint}
			onClick={item.onOpen}
		>
			{body}
		</button>
	) : (
		<div class="working" data-testid="working" data-op={item.id} aria-busy="true">
			{body}
		</div>
	);
}

/** Every registered operation, in the shell (under the header). */
export function WorkStatus() {
	const items = work.value;
	if (items.length === 0) return null;
	return (
		<div class="working-bar" data-testid="working-bar">
			{items.map((w) => (
				<Working key={w.id} item={w} />
			))}
		</div>
	);
}
